import { Button, Checkbox, Flex, Form, Space, Spin, Tooltip } from "antd"
import PropTypes from "prop-types"
import { colors, spacings } from "themes/variables"
import { Text } from "ui/Typography"
import { useDispatch, useSelector } from "react-redux"
import { useState } from "react"
import { adjustBookingPrice, getClientBookingDetails } from "store/bookings"
import { toaster } from "components/common/Toast"
import { useTranslation } from "react-i18next"
import PriceAndReasonFields from "./PriceAndReasonFields"
import { LoadingOutlined } from "@ant-design/icons"
import { numberToCurrency } from "helpers/string"
import CustomDrawer from "ui/Drawer"
import { useBreakpoints } from "modules/window/hooks/useBreakpoints"
import { Modal } from "ui/Modal"
import { UnsavedChangesModal } from "ui/UnsavedChangesModal"

const AdjustBookingPriceModal = ({ onOpen, onClose, currentPrice, bookingUUID, isMarketplaceBooking }) => {
  const [form] = Form.useForm()
  const [hasErrors, setHasErrors] = useState(true)
  const [newPrice, setNewPrice] = useState(null)
  const dispatch = useDispatch()
  const { loading } = useSelector((state) => state.bookings.adjustBookingPrice)
  const { t } = useTranslation()
  const breakpoint = useBreakpoints()
  const isMobile = ["xs", "xsm", "sm", "md"].includes(breakpoint)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)

  const requestClose = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedModal(true)
      return
    }
    executeClose()
  }

  const executeClose = () => {
    setShowUnsavedModal(false)
    setHasUnsavedChanges(false)
    onClose()
  }

  const isValidNewPrice = (value) => {
    const numeric = parseFloat(value)
    return value && !isNaN(numeric) && numeric > 0 && Math.abs(numeric - currentPrice) >= 0.01
  }

  const handleSubmit = (data) => {
    dispatch(
      adjustBookingPrice(bookingUUID, {
        new_price: Number(data?.amount),
        reason: data?.reason,
        notify_client: isMarketplaceBooking ? true : data?.notify_client
      })
    )
      .then((response) => {
        if (response?.success) {
          toaster.success(response?.message)
          form.resetFields()
          setHasUnsavedChanges(false)
          onClose()
          dispatch(getClientBookingDetails(bookingUUID))
        }
      })
      .catch((error) => {
        if (error?.data?.is_amount_lower) {
          setHasErrors(true)
          setNewPrice(null)
          form.setFields([{ name: "amount", errors: [error?.data?.message] }])
        }
      })
  }

  const handleFieldsChange = () => {
    const fieldErrors = form.getFieldsError().some(({ errors }) => errors.length > 0)
    const allTouched = form.isFieldsTouched(["amount", "reason"])
    setHasErrors(fieldErrors || !allTouched)
    setHasUnsavedChanges(form.isFieldsTouched())
    const amount = form.getFieldValue("amount")
    setNewPrice(isValidNewPrice(amount) ? parseFloat(amount) : null)
  }

  const priceDiff = newPrice !== null ? newPrice - currentPrice : null

  const modalContent = (
    <Spin spinning={loading} indicator={<LoadingOutlined spin />} size="large">
      <Space className="w-full" direction="vertical" size={24}>
        <Flex gap={spacings.S} style={{ backgroundColor: colors.surface1 }} className="py-xs px-s" align="center" wrap>
          <Text color={colors.textSecondaryDark} fw={600}>{t("client.client_booking.current_price")}:</Text>
          <Text color={colors.textPrimary}>{numberToCurrency(currentPrice)}</Text>
          {newPrice !== null && (
            <>
              <Text color={colors.textSecondaryDark} fw={600}>→ {t("client.client_booking.new_price")}:</Text>
              <Text color={colors.textPrimary}>{numberToCurrency(newPrice)}</Text>
              {priceDiff !== null && priceDiff !== 0 && (
                <Text color={priceDiff > 0 ? colors.success : colors.error}>
                  — {t("client.client_booking.price_difference")}: {numberToCurrency(Math.abs(priceDiff))} {priceDiff > 0 ? t("client.client_booking.price_increase") : t("client.client_booking.price_decrease")}
                </Text>
              )}
            </>
          )}
        </Flex>
        <Form
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
          onFieldsChange={handleFieldsChange}
          initialValues={{ notify_client: true }}
        >
          <PriceAndReasonFields currentPrice={currentPrice} />
          <Form.Item name="notify_client" valuePropName="checked">
            <Checkbox disabled={isMarketplaceBooking}>
              <Tooltip title={isMarketplaceBooking ? t("client.client_booking.notify_client_marketplace_tooltip") : ""}>
                {t("client.client_booking.notify_client")}
              </Tooltip>
            </Checkbox>
          </Form.Item>
          <Flex gap={spacings.S} className="mt-3xl">
            <Button className="py-l px-xl" color="primary" variant="outlined" onClick={requestClose}>
              {t("global.cancel")}
            </Button>
            <Button type="primary" className="py-l px-xl" htmlType="submit" disabled={hasErrors}>
              {t("global.submit")}
            </Button>
          </Flex>
        </Form>
      </Space>
    </Spin>
  )

  return (
    <>
      {isMobile ? (
        <CustomDrawer open={onOpen} setOpen={requestClose} title={t("client.client_booking.adjust_booking_price")}>
          {modalContent}
        </CustomDrawer>
      ) : (
        <Modal centered title={t("client.client_booking.adjust_booking_price")} open={onOpen} onCancel={requestClose} width={800}>
          {modalContent}
        </Modal>
      )}
      <UnsavedChangesModal
        open={showUnsavedModal}
        onDiscard={executeClose}
        onKeepEditing={() => setShowUnsavedModal(false)}
        zIndex={1200}
      />
    </>
  )
}

AdjustBookingPriceModal.propTypes = {
  onOpen: PropTypes.bool,
  onClose: PropTypes.func,
  currentPrice: PropTypes.number,
  bookingUUID: PropTypes.string,
  isMarketplaceBooking: PropTypes.bool
}

export default AdjustBookingPriceModal
